import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../components/common/ToastProvider";
import { useCareFlowState } from "../hooks/careflow/useCareFlowState";
import { apiGet, apiPut } from "../services/api";
import { blankPatientForm, toPatientProfile } from "../components/careflow/utils";
import CareFlow360Workspace from "../components/careflow360/CareFlow360Workspace";
import DiagnosisPanel from "../components/careflow/DiagnosisPanel";
import TreatmentPanel from "../components/careflow/TreatmentPanel";
import BillingPanel from "../components/careflow/BillingPanel";
import PharmacyPanel from "../components/careflow/PharmacyPanel";
import ManagerPatientHistory from "../components/manager/ManagerPatientHistory";
import { useViewport } from "../utils/device";
import { getRole } from "../services/auth";

import "./PatientWorkflow.css";

type PatientTab = "chart" | "diagnosis" | "treatment" | "billing" | "pharmacy";
type BillingView = "visit" | "ledger";

const TABS: { id: PatientTab; label: string }[] = [
  { id: "chart", label: "Tooth Chart" },
  { id: "diagnosis", label: "Diagnosis" },
  { id: "treatment", label: "Treatment" },
  { id: "billing", label: "Billing" },
  { id: "pharmacy", label: "Pharmacy" },
];

export default function PatientWorkflow() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const viewport = useViewport();
  const isTouchViewport = viewport === "mobile" || viewport === "tablet";

  const state = useCareFlowState();
  const [activeTab, setActiveTab] = useState<PatientTab>("chart");
  const [billingView, setBillingView] = useState<BillingView>("visit");
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editingCarestream, setEditingCarestream] = useState(false);
  const [tempCarestream, setTempCarestream] = useState("");

  const handleSaveCarestream = async () => {
    if (!state.patient?.patient_id) return;
    try {
      await apiPut(`/api/patients/${state.patient.patient_id}`, { carestream_id: tempCarestream });
      if (state.bindPatientContext) {
         state.bindPatientContext({ ...state.patient, carestream_id: tempCarestream });
      }
      setEditingCarestream(false);
      showToast({ message: "Carestream ID saved successfully.", type: "success" });
    } catch (e) {
      showToast({ message: "Failed to save Carestream ID.", type: "error" });
    }
  };

  const patientIdValue = useMemo(
    () => (state.patient?.patient_id ? String(state.patient.patient_id) : patientId || ""),
    [state.patient?.patient_id, patientId]
  );

  const visitIdValue = useMemo(
    () => (state.visit?.visit_id != null ? Number(state.visit.visit_id) : null),
    [state.visit?.visit_id]
  );

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setLoadingPatient(true);
    setLoadError(null);

    apiGet(`/api/patients/${encodeURIComponent(patientId)}`)
      .then((res) => {
        if (cancelled) return;
        const profile = toPatientProfile(res, blankPatientForm);
        if (!profile.patient_id) {
          throw new Error("Missing patient id");
        }
        return state.bindPatientContext(profile);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("Unable to load patient details. Please verify the patient ID.");
        showToast({ message: "Failed to load patient profile.", type: "error" });
      })
      .finally(() => {
        if (!cancelled) setLoadingPatient(false);
      });

    return () => {
      cancelled = true;
    };
  }, [patientId, showToast, state.bindPatientContext]);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      const role = getRole();
      if (role === 'doctor') navigate('/doctor');
      else if (role === 'manager') navigate('/manager');
      else if (role === 'admin') navigate('/admin');
      else if (role === 'pharmacy') navigate('/pharmacy');
      else navigate('/reception');
    }
  };

  const patientName = state.patient?.name || "Patient";
  const patientMeta = [
    state.patient?.mobile || state.patient?.phone || "No phone",
    state.patient?.gender ? String(state.patient.gender) : "--",
    state.patient?.age ? `${state.patient.age}y` : "--",
  ].join(" | ");

  if (!patientId) {
    return (
      <div className="pw-page">
        <div className="pw-state">No patient selected.</div>
      </div>
    );
  }

  return (
    <div className="pw-page">
      <header className="pw-header">
        <div className="pw-header-left">
          <button className="pw-back" type="button" onClick={handleBack}>
            ← Back
          </button>
          <div className="pw-title">
            <h1>{patientName}</h1>
            <span className="pw-id">#{patientIdValue || "--"}</span>
            {editingCarestream ? (
              <div className="pw-carestream-edit">
                <input 
                  autoFocus
                  value={tempCarestream}
                  onChange={(e) => setTempCarestream(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCarestream(); if (e.key === 'Escape') setEditingCarestream(false); }}
                  placeholder="ID (e.g. SE0081)"
                  className="pw-carestream-input"
                />
                <button onClick={handleSaveCarestream} className="pw-carestream-save">Save</button>
              </div>
            ) : state.patient?.carestream_id ? (
              <span 
                onClick={() => { setTempCarestream(state.patient?.carestream_id || ""); setEditingCarestream(true); }}
                className="pw-carestream-tag pw-carestream-tag-active"
                title="Click to edit Carestream ID"
              >
                RVG: {state.patient.carestream_id} ✎
              </span>
            ) : (
              <span 
                onClick={() => { setTempCarestream(""); setEditingCarestream(true); }}
                className="pw-carestream-tag pw-carestream-tag-empty"
              >
                + Add Carestream ID
              </span>
            )}
          </div>
          <div className="pw-meta">{patientMeta}</div>
        </div>
      </header>

      <nav className="pw-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <section className="pw-content">
        {loadingPatient && (
          <div className="pw-state">Loading patient workflow...</div>
        )}
        {loadError && !loadingPatient && (
          <div className="pw-error">{loadError}</div>
        )}

        {!loadingPatient && !loadError && (
          <>
            {activeTab === "chart" && (
              <div className="pw-panel">
                {visitIdValue ? (
                  <CareFlow360Workspace
                    visitId={visitIdValue}
                    patientId={patientIdValue}
                    patientName={state.patient?.name}
                    onOpenDiagnosis={() => setActiveTab("diagnosis")}
                    onOpenTreatment={() => setActiveTab("treatment")}
                    onOpenBilling={() => setActiveTab("billing")}
                    onOpenPharmacy={() => setActiveTab("pharmacy")}
                  />
                ) : (
                  <div className="mod-empty-state pw-empty-card">
                    <h3>No active visit</h3>
                    <p>Start a visit from reception or select a scheduled appointment to open charting.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "diagnosis" && (
              <DiagnosisPanel
                visitIdValue={visitIdValue}
                patientIdValue={patientIdValue}
                diagnosisOptions={state.diagnosisOptions}
                setDiagnosisOptions={state.setDiagnosisOptions}
                diagnosisMasterOptions={state.diagnosisMaster}
                commonDiagnoses={state.commonDiagnoses}
                visitDiagnoses={state.visitDiagnoses}
                setVisitDiagnoses={state.setVisitDiagnoses}
                showToast={showToast}
              />
            )}

            {activeTab === "treatment" && (
              <TreatmentPanel
                visitIdValue={visitIdValue}
                patientIdValue={patientIdValue}
                catalog={state.catalog}
                catalogLoading={state.catalogLoading}
                consultants={state.consultants}
                consultantServices={state.consultantServices}
                consultantServiceFees={state.consultantServiceFees}
                sessions={state.sessions}
                setSessions={state.setSessions}
                showToast={showToast}
                isTouchViewport={isTouchViewport}
              />
            )}

            {activeTab === "billing" && (
              <div className="pw-billing">
                <div className="pw-subtabs">
                  <button
                    type="button"
                    className={billingView === "visit" ? "active" : ""}
                    onClick={() => setBillingView("visit")}
                  >
                    Visit Bill
                  </button>
                  <button
                    type="button"
                    className={billingView === "ledger" ? "active" : ""}
                    onClick={() => setBillingView("ledger")}
                  >
                    Patient Ledger
                  </button>
                </div>
                {billingView === "visit" ? (
                  <BillingPanel patient={state.patient} visit={state.visit} />
                ) : (
                  <div className="pw-ledger-card">
                    <ManagerPatientHistory patient={state.patient} />
                  </div>
                )}
              </div>
            )}

            {activeTab === "pharmacy" && (
              <PharmacyPanel patientIdValue={patientIdValue} showToast={showToast} />
            )}
          </>
        )}
      </section>
    </div>
  );
}
